import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const getTranslateOnlyTransform = (transform) => {
  if (!transform) return undefined;
  return `translate3d(${transform.x}px, ${transform.y}px, 0)`;
};

const Tag = ({ tag, onRemove, attributes, listeners, setNodeRef, isOverlay = false }) => {
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        backgroundColor: "#402680",
        borderRadius: "2px",
        border: "1px solid #402680",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        fontSize: "15px",
        fontWeight: 400,
        marginBottom: "5px",
        marginRight: "5px",
        padding: "1px 5px",
        cursor: isOverlay ? "grabbing" : "grab",
        opacity: isOverlay ? 0.8 : 1,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ cursor: isOverlay ? "grabbing" : "grab" }} {...listeners}>
        {tag}
      </span>
      <span
        onClick={(e) => {
          e.stopPropagation();
          onRemove?.(tag);
        }}
        style={{
          marginLeft: "10px",
          color: "#79D9D9",
          fontWeight: "bold",
          cursor: "pointer",
          userSelect: "none",
          fontSize: "12px",
        }}
        role="button"
        tabIndex={0}
      >
        X
      </span>
    </div>
  );
};

const SortableTag = ({ tag, onRemove, activeId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tag });

  const style = {
    transform: getTranslateOnlyTransform(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div style={style}>
      <Tag
        tag={tag}
        onRemove={onRemove}
        attributes={attributes}
        listeners={listeners}
        setNodeRef={setNodeRef}
      />
    </div>
  );
};

const DraggableTagsInput = ({
  value,
  onChange,
  error = false,
  placeholder = "Add new...",
}) => {
  const [input, setInput] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && input.trim()) {
      e.preventDefault();
      const newTag = input.trim();
      if (!value.includes(newTag)) {
        onChange([...value, newTag]);
      }
      setInput("");
    }
  };

  const handleRemove = (tag) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = value.indexOf(active.id);
    const newIndex = value.indexOf(over.id);
    onChange(arrayMove(value, oldIndex, newIndex));
  };

  return (
    <div
      style={{
        border: isFocused
          ? "2px solid #79D9D9"
          : `2px solid ${error ? "#e74c3c" : "#ddd"}`,
        boxShadow: isFocused
          ? "inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px rgba(233, 102, 163, 0.6)"
          : "none",
        outline: "none",
        borderRadius: "6px",
        padding: "8px",
        minHeight: "80px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px",
        backgroundColor: "#fff",
        cursor: "text",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      }}
      onClick={() => document.getElementById("tag-input")?.focus()}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={value}>
          {value.map((tag) => (
            <SortableTag key={tag} tag={tag} onRemove={handleRemove} activeId={activeId} />
          ))}
        </SortableContext>

        <DragOverlay>
          {activeId && <Tag tag={activeId} onRemove={() => {}} isOverlay />}
        </DragOverlay>
      </DndContext>

      <input
        id="tag-input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        style={{
          border: "none",
          outline: "none",
          fontSize: "14px",
          flex: "1",
          minWidth: "120px",
          padding: "6px",
        }}
      />
    </div>
  );
};

export default DraggableTagsInput;
